import { computed, inject, Signal } from '@angular/core';
import { isActive, Router } from '@angular/router';
import { CURRENT_USER } from '@shared/tokens/current-user.token';
import { Role } from '@shared/enums/role.enum';
import { FollowUpBadgeService } from '../../shared/services/local/follow-up-badge.service';
import { AppNavigation, NavItem } from '@shared/types/navigation.types';
import {
    ACCOUNTING_URLS,
    ADMINISTRATION_URLS,
    BANKING_URLS,
    BRANCHES_URLS,
    BUILDINGS_URLS,
    CONTACTS_URLS,
    CONTRACTORS_URLS,
    CYCLE_URLS,
    INSPECTIONS_URLS,
    MEETINGS_URLS,
    NOTIFICATIONS_URLS,
    PROPERTIES_URLS,
    ROOT_URLS,
    UNITS_URLS,
    EMAILS_URLS,
    FOLLOW_UPS_URLS,
    TASKS_URLS,
    DOCUMENTS_URLS,
    AGENT_CONFIGURATION_URLS,
    MAILBOXES_URLS,
    DRIVE_IMPORTS_URLS,
} from '../constants/urls.constants';

const EXACT = { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const;

const SUBSET = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const;

/**
 * Gets the navigation for the application.
 *
 * @returns The navigation for the application.
 */
export function getNavigation(): Signal<AppNavigation> {
    const currentUser = inject(CURRENT_USER);
    const router = inject(Router);

    // Eagerly created here: the badge must count due Wiedervorlagen from app start, not
    // only once the follow-ups page was visited
    const followUpBadge = inject(FollowUpBadgeService);

    return computed<AppNavigation>(() => {
        // Configuration areas are ADMIN-ONLY: the api rejects non-admins anyway (403), the
        // navigation simply does not advertise doors that are locked
        const isAdministrator = currentUser()?.roles?.some((role) => role === Role.Administrator) ?? false;

        // Typed BEFORE the spread: inside the conditional the array literal loses its
        // literal context and TS widens icon to string — NavItem[] keeps it checked
        const adminConfigurationItems: NavItem[] = isAdministrator ? [
            {
                id: 'email-intelligence-mailboxes',
                icon: 'gear',
                label: 'Navigation.Mailboxes',
                route: MAILBOXES_URLS.list,
                isActive: isActive(MAILBOXES_URLS.list, router, SUBSET),
            },
            {
                id: 'email-intelligence-agent-configuration',
                icon: 'lightning',
                label: 'Navigation.AgentConfiguration',
                route: AGENT_CONFIGURATION_URLS.form,
                isActive: isActive(AGENT_CONFIGURATION_URLS.form, router, SUBSET),
            },
            {
                id: 'email-intelligence-agent-rules',
                icon: 'lightning',
                label: 'Navigation.AgentRules',
                route: AGENT_CONFIGURATION_URLS.rules,
                isActive: isActive(AGENT_CONFIGURATION_URLS.rules, router, SUBSET),
            },
            {
                id: 'email-intelligence-tenant-workflows',
                icon: 'share-network',
                label: 'Navigation.TenantWorkflows',
                route: AGENT_CONFIGURATION_URLS.workflows,
                isActive: isActive(AGENT_CONFIGURATION_URLS.workflows, router, SUBSET),
            },
            {
                id: 'email-intelligence-drive-imports',
                icon: 'upload-simple',
                label: 'Navigation.DriveImports',
                route: DRIVE_IMPORTS_URLS.list,
                isActive: isActive(DRIVE_IMPORTS_URLS.list, router, SUBSET),
            },
        ] : [];

        return [
        {
            id: 'dashboard',
            icon: 'gauge',
            label: 'Navigation.Dashboard',
            route: ROOT_URLS.dashboard,
            isActive: isActive(ROOT_URLS.dashboard, router, EXACT),
        },
        {
            id: 'email-intelligence',
            icon: 'envelope-open',
            label: 'Navigation.EmailIntelligence',
            expandedByDefault: true,
            children: [
                {
                    id: 'email-intelligence-emails',
                    icon: 'alternate-email',
                    label: 'Navigation.Emails',
                    route: EMAILS_URLS.list,
                    isActive: isActive(EMAILS_URLS.list, router, SUBSET),
                },
                {
                    id: 'email-intelligence-follow-ups',
                    icon: 'clock-countdown',
                    label: 'Navigation.FollowUps',
                    route: FOLLOW_UPS_URLS.list,
                    isActive: isActive(FOLLOW_UPS_URLS.list, router, EXACT),
                    // Falsy hides the chip — the badge only shouts when something is DUE
                    badge: () => followUpBadge.dueCount() || null,
                },
                {
                    id: 'email-intelligence-tasks',
                    icon: 'check-square',
                    label: 'Navigation.Tasks',
                    route: TASKS_URLS.board,
                    isActive: isActive(TASKS_URLS.board, router, SUBSET),
                },
                {
                    id: 'email-intelligence-documents',
                    icon: 'file-text',
                    label: 'Navigation.Documents',
                    route: DOCUMENTS_URLS.list,
                    isActive: isActive(DOCUMENTS_URLS.list, router, SUBSET),
                },
                ...adminConfigurationItems,
                {
                    // SELF-SERVICE: every employee, not admin-gated — the page edits only
                    // the current user's own AI writing style
                    id: 'email-intelligence-my-style',
                    icon: 'pencil',
                    label: 'Navigation.MyStyle',
                    route: AGENT_CONFIGURATION_URLS.myStyle,
                    isActive: isActive(AGENT_CONFIGURATION_URLS.myStyle, router, SUBSET),
                },
            ],
        },
        {
            id: 'master-data',
            icon: 'buildings',
            label: 'Navigation.MasterData',
            expandedByDefault: true,
            children: [
                {
                    id: 'master-data-properties',
                    icon: 'buildings',
                    label: 'Navigation.Properties',
                    route: PROPERTIES_URLS.list,
                    isActive: isActive(PROPERTIES_URLS.list, router, SUBSET),
                },
                {
                    id: 'master-data-buildings',
                    icon: 'house-simple',
                    label: 'Navigation.Buildings',
                    route: BUILDINGS_URLS.list,
                    isActive: isActive(BUILDINGS_URLS.list, router, SUBSET),
                },
                {
                    id: 'master-data-units',
                    icon: 'folder-open',
                    label: 'Navigation.Units',
                    route: UNITS_URLS.list,
                    isActive: isActive(UNITS_URLS.list, router, SUBSET),
                },
                {
                    id: 'master-data-contacts',
                    icon: 'users-three',
                    label: 'Navigation.Contacts',
                    route: CONTACTS_URLS.list,
                    isActive: isActive(CONTACTS_URLS.list, router, SUBSET),
                },
                {
                    id: 'master-data-contractors',
                    icon: 'toolbox',
                    label: 'Navigation.Contractors',
                    route: CONTRACTORS_URLS.list,
                    isActive: isActive(CONTRACTORS_URLS.list, router, SUBSET),
                },
                // Hidden for now — the Filialen screens stay routable, only the menu entry is gone.
                // {
                //     id: 'master-data-branches',
                //     icon: 'globe',
                //     label: 'Navigation.Branches',
                //     route: BRANCHES_URLS.list,
                //     isActive: isActive(BRANCHES_URLS.list, router, SUBSET),
                // },
            ],
        },
        {
            id: 'accounting',
            icon: 'database',
            label: 'Navigation.Accounting',
            expandedByDefault: true,
            children: [
                {
                    id: 'accounting-bookings',
                    icon: 'list',
                    label: 'Navigation.Journal',
                    route: ACCOUNTING_URLS.bookings,
                    isActive: isActive(ACCOUNTING_URLS.bookings, router, SUBSET),
                },
                {
                    id: 'accounting-accounts',
                    icon: 'stack',
                    label: 'Navigation.Accounts',
                    route: ACCOUNTING_URLS.accounts,
                    isActive: isActive(ACCOUNTING_URLS.accounts, router, SUBSET),
                },
                {
                    id: 'accounting-fiscal-years',
                    icon: 'calendar-blank',
                    label: 'Navigation.FiscalYears',
                    route: ACCOUNTING_URLS.fiscalYears,
                    isActive: isActive(ACCOUNTING_URLS.fiscalYears, router, SUBSET),
                },
                {
                    id: 'accounting-distribution-keys',
                    icon: 'circles-three',
                    label: 'Navigation.DistributionKeys',
                    route: ACCOUNTING_URLS.distributionKeys,
                    isActive: isActive(ACCOUNTING_URLS.distributionKeys, router, SUBSET),
                },
            ],
        },
        {
            id: 'banking',
            icon: 'arrows-left-right',
            label: 'Navigation.Banking',
            expandedByDefault: true,
            children: [
                {
                    id: 'banking-accounts',
                    icon: 'stack',
                    label: 'Navigation.PropertyBankAccounts',
                    route: BANKING_URLS.accounts,
                    isActive: isActive(BANKING_URLS.accounts, router, SUBSET),
                },
                {
                    id: 'banking-matches',
                    icon: 'unite',
                    label: 'Navigation.Matches',
                    route: BANKING_URLS.matches,
                    isActive: isActive(BANKING_URLS.matches, router, SUBSET),
                },
                {
                    id: 'banking-transactions',
                    icon: 'arrows-vertical',
                    label: 'Navigation.BankTransactions',
                    route: BANKING_URLS.transactions,
                    isActive: isActive(BANKING_URLS.transactions, router, SUBSET),
                },
                {
                    id: 'banking-matching-rules',
                    icon: 'funnel',
                    label: 'Navigation.MatchingRules',
                    route: BANKING_URLS.matchingRules,
                    isActive: isActive(BANKING_URLS.matchingRules, router, SUBSET),
                },
                {
                    id: 'banking-payment-batches',
                    icon: 'paper-plane-right',
                    label: 'Navigation.PaymentBatches',
                    route: BANKING_URLS.paymentBatches,
                    isActive: isActive(BANKING_URLS.paymentBatches, router, SUBSET),
                },
                {
                    id: 'banking-sepa-mandates',
                    icon: 'clipboard',
                    label: 'Navigation.SepaMandates',
                    route: BANKING_URLS.sepaMandates,
                    isActive: isActive(BANKING_URLS.sepaMandates, router, SUBSET),
                },
                {
                    id: 'banking-connections',
                    icon: 'broadcast',
                    label: 'Navigation.BankConnections',
                    route: BANKING_URLS.connections,
                    isActive: isActive(BANKING_URLS.connections, router, SUBSET),
                },
                {
                    id: 'banking-banks',
                    icon: 'buildings',
                    label: 'Navigation.Banks',
                    route: BANKING_URLS.banks,
                    isActive: isActive(BANKING_URLS.banks, router, SUBSET),
                },
            ],
        },
        {
            id: 'cycle',
            icon: 'arrow-counter-clockwise',
            label: 'Navigation.Cycle',
            expandedByDefault: true,
            children: [
                {
                    id: 'cycle-budget-plans',
                    icon: 'note-pencil',
                    label: 'Navigation.BudgetPlans',
                    route: CYCLE_URLS.budgetPlans,
                    isActive: isActive(CYCLE_URLS.budgetPlans, router, SUBSET),
                },
                {
                    id: 'cycle-sollstellung',
                    icon: 'clock-countdown',
                    label: 'Navigation.Sollstellung',
                    route: CYCLE_URLS.sollstellung,
                    isActive: isActive(CYCLE_URLS.sollstellung, router, SUBSET),
                },
                {
                    id: 'cycle-annual-statements',
                    icon: 'file-text',
                    label: 'Navigation.AnnualStatements',
                    route: CYCLE_URLS.annualStatements,
                    isActive: isActive(CYCLE_URLS.annualStatements, router, SUBSET),
                },
                {
                    id: 'cycle-meetings',
                    icon: 'users-four',
                    label: 'Navigation.Meetings',
                    route: MEETINGS_URLS.list,
                    isActive: isActive(MEETINGS_URLS.list, router, SUBSET),
                },
                {
                    id: 'cycle-inspections',
                    icon: 'eye',
                    label: 'Navigation.Inspections',
                    route: INSPECTIONS_URLS.list,
                    isActive: isActive(INSPECTIONS_URLS.list, router, SUBSET),
                },
            ],
        },
        {
            id: 'communication',
            icon: 'megaphone',
            label: 'Navigation.Communication',
            expandedByDefault: true,
            children: [
                {
                    id: 'communication-notifications',
                    icon: 'envelope-open',
                    label: 'Navigation.Notifications',
                    route: NOTIFICATIONS_URLS.list,
                    isActive: isActive(NOTIFICATIONS_URLS.list, router, SUBSET),
                },
            ],
        },
        {
            id: 'administration',
            icon: 'gear',
            label: 'Navigation.Administration',
            children: [
                {
                    id: 'administration-platform-users',
                    icon: 'user-circle',
                    label: 'Navigation.PlatformUsers',
                    route: ADMINISTRATION_URLS.platformUsers,
                    isActive: isActive(ADMINISTRATION_URLS.platformUsers, router, SUBSET),
                },
                {
                    id: 'administration-permissions',
                    icon: 'check-square',
                    label: 'Navigation.Permissions',
                    route: ADMINISTRATION_URLS.permissions,
                    isActive: isActive(ADMINISTRATION_URLS.permissions, router, SUBSET),
                },
                {
                    id: 'administration-webhooks',
                    icon: 'share-network',
                    label: 'Navigation.Webhooks',
                    route: ADMINISTRATION_URLS.webhooks,
                    isActive: isActive(ADMINISTRATION_URLS.webhooks, router, SUBSET),
                },
                {
                    id: 'administration-tenant-domains',
                    icon: 'link-simple',
                    label: 'Navigation.TenantDomains',
                    route: ADMINISTRATION_URLS.tenantDomains,
                    isActive: isActive(ADMINISTRATION_URLS.tenantDomains, router, SUBSET),
                },
                {
                    id: 'administration-email-templates',
                    icon: 'envelope-open',
                    label: 'Navigation.EmailTemplates',
                    route: ADMINISTRATION_URLS.emailTemplates,
                    isActive: isActive(ADMINISTRATION_URLS.emailTemplates, router, SUBSET),
                },
                {
                    id: 'administration-settings',
                    icon: 'toolbox',
                    label: 'Navigation.Settings',
                    route: ADMINISTRATION_URLS.settings,
                    isActive: isActive(ADMINISTRATION_URLS.settings, router, SUBSET),
                },
                {
                    id: 'administration-dms',
                    icon: 'lock-key',
                    label: 'Navigation.DmsGovernance',
                    route: ADMINISTRATION_URLS.dms,
                    isActive: isActive(ADMINISTRATION_URLS.dms, router, SUBSET),
                },
            ],
        },
    ];
    });
}